/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason,
  Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { VTextField, VForm, useVForm, IVFormErrors, VSwitch, AutoCompleteParceiro, VTextFieldData, VFieldMoney, AutoCompleteProdutor, BasicSelect, VSwitch01, AutoCompletePlanoDeContas } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { ParceirosService } from '../../../shared/services/api/cadastros/ParceirosService';
import { IComprovanteBaixaDados } from '../ContasReceber/ComprovanteBaixa';
import { DialogVisualizarComprovante } from '../ContasReceber/DialogVisualizarComprovante';
import { montarReciboPagamento, montarRecibosDasParcelas } from './ReciboPagamento';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { ContasPagarService, IParcelaLancamentoPagar } from '../../../shared/services/api/Financeiro/ContasPagar';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { VFieldMoney4Faturamento } from '../../Estoque/entradaEstoque/VFieldMoney4Faturamento';

// Uma parcela de um lançamento novo, gerada abaixo do formulário quando o
// usuário escolhe mais de 1 parcela — cada uma com seu número de documento
// (vazio = usa o Numero Documento do lançamento), vencimento e valor.
interface IParcelaNova {
  idpagar: string;
  numero: string;
  vencimento: Date;
  vparcela: number;
}

const arredondar2 = (valor: number) => Math.round(valor * 100) / 100;

// Divide o total em `qtd` parcelas de 2 casas; a diferença do arredondamento
// vai pra última, pra soma bater exatamente com o total.
const dividirValor = (total: number, qtd: number): number[] => {
  const base = arredondar2(total / qtd);
  const valores = Array.from({ length: qtd }, () => base);
  valores[qtd - 1] = arredondar2(total - base * (qtd - 1));
  return valores;
};

interface IFormData {
  emissao: Date;
  vencimento: Date;
  vpagar: number;
  vpago: number;
  vparcela: number;
  vcompra: number;
  parcela: string;
  idplanoconta: string;
  numero: string;
  tipopagamento: string;
  tipo: number;
  desconto: number;
  acrescimo: number;
  historico: string;
  idparceiro: string;
}

const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'dataoperacao'>> = yup.object().shape({
  idparceiro: yup.string().required(),
  idplanoconta: yup.string().required(),
  tipopagamento: yup.string().required(),
  parcela: yup.string().required(),
  emissao: yup.date().required(),
  vencimento: yup.date().required('O campo é obrigatório')
    .when('$isEditMode', {
      is: false,
      then: () => yup.date().test('valid-date', 'Não se Lança conta já Vencida', function (value) {
        if (!value) {
          return this.createError({ message: 'Data inválida' });
        }
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() - 1);
        return value >= currentDate;
      }),
    }),
  historico: yup.string().optional().default(''),
  // Com parcelas geradas abaixo do formulário, o número vem de cada parcela
  // (ver salvarParcelas) e o campo do cabeçalho some — não é exigido.
  numero: yup.string().optional().default('')
    .test('numero-documento', function (value) {
      if (this.options.context?.temParcelas) return true;
      if (!value) return this.createError({ message: 'O campo é obrigatório' });
      if (value.length > 30) return this.createError({ message: 'O campo pode ter no máximo 30 caracteres' });
      return true;
    }),
  vparcela: yup
    .number()
    .required()
    .test(
      'valorparcela',
      'o valor deve ser menor ou igual ao valor total e diferente de zero',
      function (value) {
        const vcompra = Number(this.parent.vcompra || 0);
        const parcela = Number(value || 0);

        const parcelaFixed = Number(parcela.toFixed(2));
        const vcompraFixed = Number(vcompra.toFixed(2));

        return parcelaFixed <= vcompraFixed && parcelaFixed > 0;
      }
    ),
  acrescimo: yup.number().required(),
  desconto: yup
    .number().required()
    .test(
      'desconto-maximo',
      'Desconto maior que o permitido',
      function (value) {
        const parcela = Number(this.parent.vparcela || 0);
        const acrescimo = Number(this.parent.acrescimo || 0);
        const vpago = Number(this.parent.vpago || 0);

        const maximo = parcela + acrescimo - vpago;

        return Number(value || 0) <= maximo;
      }
    ),
  vpago: yup.number().required(),
  vpagar: yup
    .number()
    .required()
    .min(0, 'O valor não pode ser negativo'),
  vcompra: yup.number().required(),


  tipo: yup.number().optional().default(2),
});

export const DetalheDePagar: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const location = useLocation();
  const voltarParaListagem = (location.state as { from?: string } | null)?.from || '/contaspagar';
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [descricao, setDescricao] = useState('');
  const [statuss, setStatuss] = useState(false);
  // Recibo de pagamento pro parceiro assinar — antes da baixa (saldo em
  // aberto) e depois (valor e forma dos pagamentos). Ver ReciboPagamento.ts.
  const [contaCarregada, setContaCarregada] = useState<{ idparceiro: string; numero: string; parcela: string; historico: string; vpagar: number } | null>(null);
  const [reciboDados, setReciboDados] = useState<IComprovanteBaixaDados | null>(null);
  const [openRecibo, setOpenRecibo] = useState(false);
  const [gerandoRecibo, setGerandoRecibo] = useState(false);
  // Parceiro já buscado no 1º recibo — reaproveitado em "Todas as parcelas".
  const [parceiroRecibo, setParceiroRecibo] = useState<{ nome: string; cpf?: string }>({ nome: '' });
  const [statusEntrada, setStatusEntrada] = useState(false);
  const [statusPago, setStatusPgo] = useState(false);
  const [pagas, setPagas] = useState(0);
  const [parcelasNovas, setParcelasNovas] = useState<IParcelaNova[]>([]);
  // Muda só quando os valores são recalculados pelo sistema (novo total),
  // pra remontar os campos de valor — VFieldMoney4Faturamento só lê o
  // defaultValue na montagem. Digitar num valor NÃO muda isso (perderia o foco).
  const [geracaoValores, setGeracaoValores] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [openConfirm, setOpenConfirm] = useState(false);
  const [apagando, setApagando] = useState(false);
  // Todas as parcelas do lançamento (mesmo parceiro + número) — apagar leva
  // todas juntas, e se qualquer uma tiver pagamento o Apagar fica bloqueado.
  const [parcelasLancamento, setParcelasLancamento] = useState<IParcelaLancamentoPagar[]>([]);
  const lancamentoComPagamento = parcelasLancamento.some(p => p.status || Number(p.pagas) > 0);


  const parseMoney = (value: string | undefined) => {
    if (!value) return 0;

    return Number(
      value
        .replace(/\./g, '')
        .replace(',', '.')
    );
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      navigate(voltarParaListagem);
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true);
  };

  // Sempre apaga o lançamento inteiro (todas as parcelas vinculadas) —
  // não existe apagar uma parcela sozinha.
  const handleConfirmDelete = () => {
    if (apagando) return;
    setApagando(true);
    ContasPagarService.deleteById(id)
      .then(result => {
        setApagando(false);
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg(parcelasLancamento.length > 1 ? 'Todas as parcelas foram apagadas com sucesso!' : 'Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };

  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  const imprimirRecibo = async () => {
    if (!contaCarregada || id === 'novo') return;
    setGerandoRecibo(true);
    const parceiro = contaCarregada.idparceiro ? await ParceirosService.getById(contaCarregada.idparceiro) : undefined;
    const dadosParceiro = parceiro && !(parceiro instanceof Error) ? { nome: parceiro.nome, cpf: parceiro.cpf } : { nome: '' };
    setParceiroRecibo(dadosParceiro);
    const recibo = await montarReciboPagamento({
      idpagar: id,
      parceiro: dadosParceiro.nome,
      documentoParceiro: dadosParceiro.cpf,
      numero: contaCarregada.numero,
      parcela: contaCarregada.parcela,
      historico: contaCarregada.historico,
      valorEmAberto: contaCarregada.vpagar,
    });
    setGerandoRecibo(false);
    if (recibo instanceof Error) {
      setTms('error');
      setMsg(recibo.message);
      setOpenmsg(true);
      return;
    }
    setReciboDados(recibo);
    setOpenRecibo(true);
  };

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ContasPagarService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setDescricao('Lançamento Contas Pagar');
            formRef.current?.setData(result);
            setContaCarregada({
              idparceiro: result.idparceiro, numero: result.numero, parcela: result.parcela,
              historico: result.historico, vpagar: Number(result.vpagar),
            });
            setParcelasLancamento(result.parcelas || []);
            setPagas(result.vcompra);
            if (result.tipo == 1) {
              setStatusEntrada(true);
            } else {
              setStatusEntrada(false);
            }
            if (result.vcompra > 0) {
              setStatusPgo(true);
            } else {
              setStatusPgo(false);
            }
          }
        });
    } else {
      // Parceiro filtrado na listagem (aba Parceiro ou Pagas por período,
      // ver aoClicarEmNovo em ListagemContasPagar.tsx) já vem pré-
      // selecionado pra um novo lançamento — mesmo padrão de
      // DetalhesDeReceber.tsx.
      const idParceiroPreSelecionado = (location.state as { idparceiro?: string } | null)?.idparceiro;
      formRef.current?.setData({
        idparceiro: idParceiroPreSelecionado,
        tipopagamento: 'carteira',
        emissao: new Date(),
        vencimento: new Date(),
        parcela: '1',
        numero: '',
        vparcela: 0,
        vcompra: 0,
        acrescimo: 0,
        vpago: 0,
        desconto: 0,
        vpagar: 0,
        historico: '',

      });
    }
  }, [id]);

  // Gera (ou apaga) a lista de parcelas ao escolher a quantidade: valores
  // divididos do Valor Total e vencimentos mês a mês a partir do Vencimento
  // do formulário. Número de documento começa vazio (= o do lançamento).
  // "Valor Parcela" do cabeçalho mostra o valor de UMA parcela (o total
  // dividido), não o total — e o Valor Pagar acompanha.
  const atualizarValorParcelaCabecalho = (valorParcela: number) => {
    const data = formRef.current?.getData();
    const acrescimo = Number(data?.acrescimo || 0);
    const desconto = Number(data?.desconto || 0);
    const vpago = Number(data?.vpago || 0);
    formRef.current?.setFieldValue('vparcela', valorParcela);
    formRef.current?.setFieldValue('vpagar', Math.max(Number((valorParcela + acrescimo - desconto - vpago).toFixed(2)), 0));
  };

  const gerarParcelas = (quantidade: string | undefined) => {
    const qtd = Number(quantidade || 1);
    if (id !== 'novo' || qtd <= 1) {
      setParcelasNovas([]);
      // Voltou pra 1 parcela: o Valor Parcela volta a ser o total.
      if (id === 'novo') atualizarValorParcelaCabecalho(Number(formRef.current?.getData()?.vcompra || 0));
      return;
    }
    const data = formRef.current?.getData();
    const total = Number(data?.vcompra || 0);
    const vencimentoBase = dayjs(data?.vencimento).isValid() ? dayjs(data?.vencimento) : dayjs();
    const valores = dividirValor(total, qtd);
    setParcelasNovas(valores.map((vparcela, i) => ({
      idpagar: uuidv4(),
      numero: '',
      vencimento: vencimentoBase.add(i, 'month').toDate(),
      vparcela,
    })));
    setGeracaoValores(g => g + 1);
    atualizarValorParcelaCabecalho(valores[0]);
  };

  const alterarParcela =(idpagar: string, campos: Partial<IParcelaNova>) => {
    setParcelasNovas(atual => atual.map(p => (p.idpagar === idpagar ? { ...p, ...campos } : p)));
  };

  const salvarParcelas = (dados: IFormData) => {
    const qtd = parcelasNovas.length;
    const hoje = dayjs().startOf('day');
    const erro =
      parcelasNovas.some(p => !p.numero.trim()) ? 'Informe o Nº do Documento de todas as parcelas'
        : parcelasNovas.some(p => !dayjs(p.vencimento).isValid()) ? 'Informe o vencimento de todas as parcelas'
        : parcelasNovas.some(p => dayjs(p.vencimento).isBefore(hoje)) ? 'Não se Lança conta já Vencida'
          : parcelasNovas.some(p => !(Number(p.vparcela) > 0)) ? 'Todas as parcelas precisam ter valor'
            : parcelasNovas.some(p => p.numero.trim().length > 30) ? 'Número do documento com mais de 30 caracteres'
              : Math.abs(parcelasNovas.reduce((acc, p) => acc + Number(p.vparcela), 0) - Number(dados.vcompra)) >= 0.01
                ? 'Soma das parcelas não confere com o Valor Total Compra'
                : '';
    if (erro) {
      cancelarSalvamento();
      setIsLoading(false);
      setTms('error');
      setMsg(erro);
      setOpenmsg(true);
      return;
    }

    const idlote = uuidv4();
    const lista = parcelasNovas.map((p, i) => ({
      idlote,
      idpagar: p.idpagar,
      idparceiro: dados.idparceiro,
      idplanoconta: dados.idplanoconta,
      tipopagamento: dados.tipopagamento,
      emissao: dados.emissao,
      vencimento: p.vencimento,
      parcela: `${i + 1}/${qtd}`,
      numero: p.numero.trim(),
      vparcela: Number(p.vparcela),
      vcompra: dados.vcompra,
      acrescimo: dados.acrescimo,
      vpago: dados.vpago,
      desconto: dados.desconto,
      vpagar: Number(p.vparcela),
      historico: dados.historico,
      tipo: dados.tipo,
    }));
    ContasPagarService
      .createparcelas(lista)
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          cancelarSalvamento();
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setTms('success');
          setMsg('Lançamento Realizado com sucesso!');
          setOpenmsg(true);
          concluirSalvamento();
        }
      });
  };

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    const validationContext = {
      isEditMode: id === 'novo' ? false : true,
      temParcelas: id === 'novo' && parcelasNovas.length > 1,
    };
    formValidationSchema.
      validate(dados, { context: validationContext, abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          if (Number(dados.parcela) > 1) {
            salvarParcelas(dadosValidados);
          } else {
            ContasPagarService
              .create(dadosValidados)
              .then((result) => {
                setIsLoading(false);
                if (result instanceof Error) {
                  cancelarSalvamento();
                  setTms('error');
                  setMsg(result.message);
                  setOpenmsg(true);
                } else {
                  setTms('success');
                  setMsg('Lançamento Realizado com sucesso!');
                  setOpenmsg(true);
                  concluirSalvamento();
                }
              });
          }
        } else {
          ContasPagarService
            .updateById(id, { ...dadosValidados })
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Alterado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        }
      }).catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handlecalcularParcela = (valor: string | undefined) => {
    const tparcela = parseMoney(valor);

    const data = formRef.current?.getData();

    const acrescimo = Number(data?.acrescimo || 0);
    const desconto = Number(data?.desconto || 0);
    const vpago = Number(data?.vpago || 0);

    const vpagar = Number(
      (tparcela + acrescimo - desconto - vpago).toFixed(2)
    );

    formRef.current?.setFieldValue('vpagar', vpagar);
  };


  const handlecalcularTotal = (valor: string | undefined) => {
    const total = parseMoney(valor);

    const data = formRef.current?.getData();

    const acrescimo = Number(data?.acrescimo || 0);
    const desconto = Number(data?.desconto || 0);
    const vpago = Number(data?.vpago || 0);

    const vpagarCalculado = Number(
      (total + acrescimo - desconto - vpago).toFixed(2)
    );

    const vpagar = Math.max(vpagarCalculado, 0);

    formRef.current?.setFieldValue('vpagar', vpagar);
    formRef.current?.setFieldValue('vparcela', vpagar);

    // Parcelas já geradas: redivide o novo total, mantendo número e vencimento.
    if (parcelasNovas.length > 1) {
      const valores = dividirValor(total, parcelasNovas.length);
      setParcelasNovas(atual => atual.map((p, i) => ({ ...p, vparcela: valores[i] })));
      setGeracaoValores(g => g + 1);
      atualizarValorParcelaCabecalho(valores[0]);
    }
  };


  const handlecalcularAcescimo = (valor: string | undefined) => {
    const acrescimo = parseMoney(valor);

    const data = formRef.current?.getData();

    const parcela = Number(data?.vparcela || 0);
    const desconto = Number(data?.desconto || 0);
    const vpago = Number(data?.vpago || 0);

    const vpagar = Number(
      (parcela + acrescimo - desconto - vpago).toFixed(2)
    );

    formRef.current?.setFieldValue('vpagar', vpagar);
  };

  const handlecalcularDesconto = (valor: string | undefined) => {
    const desconto = parseMoney(valor);

    const data = formRef.current?.getData();

    const parcela = Number(data?.vparcela || 0);
    const acrescimo = Number(data?.acrescimo || 0);
    const vpago = Number(data?.vpago || 0);

    const vpagarCalculado = Number(
      (parcela + acrescimo - desconto - vpago).toFixed(2)
    );

    const vpagar = Math.max(vpagarCalculado, 0);

    formRef.current?.setFieldValue('vpagar', vpagar);
  };
  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message="Salvando..."
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Lançamento Contas Pagar' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={false}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando={isLoading}
          mostrarBotaoImpressao={id !== 'novo' && !!contaCarregada}
          mostrarBotaoImpressaoCarregando={gerandoRecibo}
          aoClicarEmImpressao={imprimirRecibo}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate(voltarParaListagem)}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/contaspagar/novo', { state: { from: voltarParaListagem } })}
          mostrarBotaoNovoCarregando={isLoading}
          disableBotaoApagar={statuss || lancamentoComPagamento}
          disableBotaoSalvar={statuss || isSaving}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        {/* Confirmação de exclusão: antes de perguntar, mostra TODAS as
            parcelas do lançamento que vão junto com esta — parcelas não
            podem ser apagadas separadamente (ver DeleteById.ts, backend). */}
        <Dialog open={openConfirm} onClose={() => { if (!apagando) handleCancelDelete(); }} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {parcelasLancamento.length > 1 ? 'Apagar lançamento parcelado' : 'Apagar conta a pagar'}
          </DialogTitle>
          <DialogContent dividers>
            {parcelasLancamento.length > 1 ? (
              <>
                <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                  Esta conta faz parte de um lançamento com <b>{parcelasLancamento.length} parcelas</b>.
                  Parcelas não podem ser apagadas separadamente — todas abaixo serão apagadas juntas.
                </Alert>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Parcela</TableCell>
                      <TableCell>Nº Documento</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell align="right">Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parcelasLancamento.map((p) => {
                      const atual = p.idpagar === id;
                      return (
                        <TableRow key={p.idpagar} selected={atual}>
                          <TableCell>
                            {p.parcela}
                            {atual && <Chip label="Esta" size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                          </TableCell>
                          <TableCell>{p.numero}</TableCell>
                          <TableCell>{dayjs(p.vencimento).format('DD/MM/YYYY')}</TableCell>
                          <TableCell align="right">
                            {Number(p.vparcela).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {parcelasLancamento.reduce((acc, p) => acc + Number(p.vparcela), 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Tem certeza que deseja apagar as {parcelasLancamento.length} parcelas?
                </Typography>
              </>
            ) : (
              <Typography variant="body2">Tem certeza que deseja excluir este registro?</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button color="inherit" onClick={handleCancelDelete} disabled={apagando}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={apagando}>
              {apagando ? 'Apagando...' : parcelasLancamento.length > 1 ? `Apagar ${parcelasLancamento.length} parcelas` : 'Apagar'}
            </Button>
          </DialogActions>
        </Dialog>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2}>

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid item>
              <Typography variant='h6'>Dados</Typography>
            </Grid>

            <Grid container item direction="row" spacing={1}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container item direction="row" spacing={2} >
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                    <AutoCompleteParceiro
                      isExternalLoading={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <AutoCompletePlanoDeContas
                      isExternalLoading={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={8} sm={8} md={2.5} lg={3} xl={3}>
                    <BasicSelect
                      name='tipopagamento'
                      label='Forma de Pagamento'
                      data={[
                        { nameBD: 'boleto', descricao: 'Boleto' },
                        { nameBD: 'carteira', descricao: 'Carteira' },
                        { nameBD: 'deposito', descricao: 'Depósito' },
                        { nameBD: 'debito em conta', descricao: 'Débito em conta' },
                        { nameBD: 'pix', descricao: 'PIX' },
                      ]}
                      disabled={statuss ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={4} sm={4} md={1.5} lg={1} xl={1}>
                    {id === 'novo' ? (
                      <BasicSelect
                        name='parcela'
                        label='Parcela'
                        data={[
                          { nameBD: '1', descricao: '1' },
                          { nameBD: '2', descricao: '2' },
                          { nameBD: '3', descricao: '3' },
                          { nameBD: '4', descricao: '4' },
                          { nameBD: '5', descricao: '5' },
                          { nameBD: '6', descricao: '6' },
                          { nameBD: '7', descricao: '7' },
                          { nameBD: '8', descricao: '8' },
                          { nameBD: '9', descricao: '9' },
                          { nameBD: '10', descricao: '10' },
                          { nameBD: '11', descricao: '11' },
                          { nameBD: '12', descricao: '12' },
                        ]}
                        disabled={isLoading}
                        aoClicar={gerarParcelas}
                      />
                    ) : (
                      <VTextField
                        fullWidth
                        size='small'
                        name='parcela'
                        label='Parcela'
                        disabled={true}
                      />
                    )}
                  </Grid>
                  {/* Com parcelas geradas, o número é informado em cada parcela lá embaixo. */}
                  {!(id === 'novo' && parcelasNovas.length > 1) && (
                    <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                      <VTextField
                        fullWidth
                        size='small'
                        name='numero'
                        label='Numero Documento'
                        disabled={statuss || statusEntrada || statusPago ? true : isLoading}
                      />
                    </Grid>
                  )}

                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VTextFieldData
                      fullWidth
                      size='small'
                      name='emissao'
                      label='Emissão'
                      disabled={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VTextFieldData
                      fullWidth
                      size='small'
                      name='vencimento'
                      label='Vencimento'
                      disabled={statuss ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vcompra'
                      label='Valor Total Compra'
                      calcular={handlecalcularTotal}
                      disabled={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vparcela'
                      label='Valor Parcela'
                      calcular={handlecalcularParcela}
                      // Com parcelas geradas, o valor de cada uma é editado lá embaixo.
                      disabled={statuss || statusEntrada || statusPago || (id === 'novo' && parcelasNovas.length > 1) ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='acrescimo'
                      label='Acré. / Juros'
                      calcular={handlecalcularAcescimo}
                      disabled={statuss ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='desconto'
                      label='Desconto'
                      calcular={handlecalcularDesconto}
                      disabled={statuss ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vpago'
                      label='Valor Pago'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vpagar'
                      label='Valor Pagar'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <VTextField
                      fullWidth
                      name='historico'
                      disabled={isLoading}
                      multiline
                      rows={3}
                      label='Histório'
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {id === 'novo' && parcelasNovas.length > 1 && (
              <>
                <Grid item>
                  <Typography variant='h6'>Parcelas</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Informe o Nº do Documento de cada parcela.
                  </Typography>
                </Grid>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container direction="column" spacing={1.5}>
                      {parcelasNovas.map((p, i) => (
                        <Grid container item direction="row" spacing={2} key={p.idpagar} alignItems="center">
                          <Grid item xs={3} sm={2} md={1} lg={1} xl={1}>
                            <TextField
                              fullWidth
                              size='small'
                              label='Parcela'
                              value={`${i + 1}/${parcelasNovas.length}`}
                              disabled
                            />
                          </Grid>
                          <Grid item xs={9} sm={4} md={3} lg={2} xl={2}>
                            <TextField
                              fullWidth
                              size='small'
                              label='Nº do Documento'
                              value={p.numero}
                              inputProps={{ maxLength: 30 }}
                              onChange={e => alterarParcela(p.idpagar, { numero: e.target.value })}
                              disabled={isLoading}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
                            <DatePicker
                              format='DD/MM/YYYY'
                              label='Vencimento'
                              value={dayjs(p.vencimento)}
                              onChange={novaData => alterarParcela(p.idpagar, { vencimento: novaData ? novaData.toDate() : new Date(NaN) })}
                              disabled={isLoading}
                              slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
                            <VFieldMoney4Faturamento
                              key={`${p.idpagar}-${geracaoValores}`}
                              fullWidth
                              size='small'
                              label='Valor'
                              id={p.idpagar}
                              defaultValue={String(p.vparcela)}
                              valor={(idpagar, valor) => alterarParcela(idpagar, { vparcela: Number(valor) })}
                              disabled={isLoading}
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </LocalizationProvider>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </VForm>
      <DialogVisualizarComprovante
        open={openRecibo}
        dados={reciboDados}
        onClose={() => setOpenRecibo(false)}
        qtdParcelas={parcelasLancamento.length}
        carregarTodas={() => montarRecibosDasParcelas(parcelasLancamento, {
          parceiro: parceiroRecibo.nome,
          documentoParceiro: parceiroRecibo.cpf,
          historico: contaCarregada?.historico,
        })}
      />
      </LayoutBaseDePagina>
    </>
  );
};